/* eslint-disable prefer-const */
/* eslint-disable no-shadow */
import * as d3 from 'd3'
import config from '@/config.json'

const sortElementsForDraw = (boardElements, linkElements) => {
  if (boardElements.length <= 0) return []
  let elements = boardElements
  if (linkElements.length > 0) {
    let currentIndex = 0
    let lastTrackIndex = 0
    for (let element of boardElements) {
      if (!element.isTrackElement) {
        lastTrackIndex = currentIndex
        break
      }
      currentIndex += 1
    }
    elements = [...elements.slice(0, lastTrackIndex), ...linkElements, ...elements.slice(lastTrackIndex, elements.length)]
  }
  return elements
}

const getDefaultState = () => ({
  mapD3: {},
  map: {},
  ctx: {},
  zoom: {},
  transform: d3.zoomIdentity,
  areLinksVisible: true,
  areZonesVisible: true,
  testM: 'selected map'
})

const state = getDefaultState()
const getters = {
  mapD3: (state) => state.mapD3,
  map: (state) => state.map,
  ctx: (state) => state.ctx,
  zoom: (state) => state.zoom,
  transform: (state) => state.transform,
  scale: (state, getters, rootState, rootGetters) => {
    const boardXDomain = rootGetters['selectedLevel/dimensions'].level.width
    const boardXRange = rootGetters['selectedLevel/dimensions'].board.width
    const boardYDomain = rootGetters['selectedLevel/dimensions'].level.height
    const boardYRange = rootGetters['selectedLevel/dimensions'].board.height
    // TODO: gui domain dynamically
    const guiXDomain = 100
    const guiYDomain = 100
    return {
      board: {
        y: d3.scaleLinear().domain([0, boardYDomain]).range([0, boardYRange]),
        x: d3.scaleLinear().domain([0, boardXDomain]).range([0, boardXRange])
      },
      gui: {
        y: d3.scaleLinear().domain([0, guiYDomain]).range([0, rootGetters['selectedLevel/mapHeight']]),
        x: d3.scaleLinear().domain([0, guiXDomain]).range([0, rootGetters['selectedLevel/mapWidth']]),
      }
    }
  },
  isOnTrack: (state, getters, rootGetters) => (mouseY, mouseX) => {
    let y = mouseY
    let x = mouseX
    // Transform and scale mouse coordinates to board cell
    x -= getters.transform.x
    y -= getters.transform.y
    x /= getters.transform.k
    y /= getters.transform.k
    const [newY, newX] = [getters.scale.board.y.invert(y - 50), getters.scale.board.x.invert(x - 50)]
    const snappedY = Math.round(newY)
    const snappedX = Math.round(newX)

    // Check if mouse board cell matches any board cells of tracks
    let flag = false
    rootGetters['selectedElement/boardTrackElements'].forEach((te) => {
      if (te.cell[0] === snappedY && te.cell[1] === snappedX) {
        flag = true
      }
    })
    return flag
  },
  isTrackOccupied: (state, getters, rootGetters) => (draggedEl, y, x) => {
    let flag = false
    let elements = rootGetters['selectedElement/boardImageElements']
    // If draggedElement is from board (not toolbar) then it must be removed from elements being considered
    if (draggedEl.isBoardElement) {
      elements = elements.filter((el) => el.id !== draggedEl.id)
    }
    elements.forEach((be) => {
      if (be.detectCollisionByPoint(y, x, 1)) {
        flag = true
      }
    })
    return flag
  },
  areLinksVisible: (state) => state.areLinksVisible,
  areZonesVisible: (state) => state.areZonesVisible,
  testM: (state) => state.testM,
}
const actions = {
  initMap: async ({ dispatch, getters, rootGetters }, payload) => {
    dispatch('setMapComponents', payload)
    await dispatch('bindMapFeaturesToElements')
    // Do not enable zoom, primarily when used in LevelSelect
    if (!rootGetters['selectedLevel/isZoomDisabled']) {
      await dispatch('initZoom')
    }
    dispatch('callZoomOffset')
    dispatch('draw')
  },
  setMapComponents: ({ commit }, { mapD3, map, ctx, mapCtr }) => {
    commit('setMapD3', mapD3)
    commit('setMap', map)
    commit('setCtx', ctx)
    commit('setMapCtr', mapCtr)
  },
  bindMapFeaturesToElements: async ({ dispatch, getters }) => {
    await dispatch('selectedElement/bindScale', getters.scale, { root: true })
    await dispatch('selectedElement/bindTransform', getters.transform, { root: true })
  },
  initZoom: async ({ commit, dispatch }) => {
    // This is the only mouse event in the system that is not controlled by the canvas itself in Map.vue
    const zoom = d3.zoom()
      .filter(() => {
        // Map drag events must be accompanied with shift key
        if (d3.event.type === 'mousedown') {
          return d3.event.shiftKey
        }
        return true
      })
      .scaleExtent([0.25, 5])
      .on('zoom', (() => dispatch('draw')))
    commit('setZoom', zoom)
    if (state.mapD3._groups) {
      state.mapD3.call(zoom)
      // Prevent double clicking
      state.mapD3.on('dblclick.zoom', null)
    }
  },
  callZoomOffset: ({ getters, dispatch, rootGetters }) => {
    let xOffset
    let yOffset
    const boardWidth = rootGetters['selectedLevel/dimensions'].board.width
    const boardHeight = rootGetters['selectedLevel/dimensions'].board.height
    const mapWidth = rootGetters['selectedLevel/mapWidth']
    const mapHeight = rootGetters['selectedLevel/mapHeight']

    const scaleX = mapWidth / boardWidth
    const scaleY = mapHeight / boardHeight

    const scale = Math.min(scaleX, scaleY) * 0.9

    xOffset = ((mapWidth - boardWidth * scale)) / 2
    yOffset = ((mapHeight - boardHeight * scale)) / 2

    dispatch('setTransform', d3.zoomIdentity.translate(xOffset, yOffset).scale(scale, scale).translate(0, 0))
    // Calling right away prevents jumping on first zoom
    if (!rootGetters['selectedLevel/isZoomDisabled'] && state.mapD3._groups) {
      getters.mapD3.call(getters.zoom.transform, getters.transform)
    }
  },
  draw: ({ getters, dispatch, rootGetters }) => {
    const ctx = getters.ctx
    if (ctx.fillStyle) {
      ctx.clearRect(0, 0, rootGetters['selectedLevel/mapWidth'], rootGetters['selectedLevel/mapHeight'])
      if (!getters.transform) {
        console.error('Transform must be set before initial draw')
      }
    // Check for zoom event
      if (d3.event) {
        dispatch('setTransform', d3.event.transform)
      }
      const { y, x, k } = getters.transform
      ctx.save()
      ctx.translate(x, y)
      ctx.scale(k, k)

      // Specifically draw tracks first, then draw the links, finally draw other elements
      let drewLinks = false
      let boardElements = rootGetters['selectedElement/boardElements']
      let linkElements = rootGetters['selectedElement/linkElements']
      for (let i = 0; i < boardElements.length; i++) {
        // console.log(`sMI:${i}`)
        if (!boardElements[i].isTrackElement && !drewLinks && getters.areLinksVisible) {
          for (let j = 0; j < linkElements.length; j++) {
            // console.log(`sMJ:${j}`)
            linkElements[j].draw(ctx)
            // draw hint
          }
          drewLinks = true
        }
        boardElements[i].draw(ctx)
      }

      let zoneElements = rootGetters['selectedElement/zoneElements']
      for (let i = 0; i < zoneElements.length; i++) {
        if (getters.areZonesVisible) {
          zoneElements[i].draw(ctx)
        }
      }

      // const elements = sortElementsForDraw(getters.boardElements, getters.linkElements)
      // elements.forEach((el) => el.draw(ctx))

      ctx.restore()
      // GUI elements don't need to be scaled
      rootGetters['selectedElement/guiElements'].forEach((el) => el.draw(ctx))
    }
  },
  setTransform: ({ dispatch, commit }, transform) => {
    commit('setTransform', transform)
    dispatch('selectedElement/bindTransform', transform, { root: true })
  },
  updateMapElements: async ({ getters, dispatch }, { width, height }) => {
    await dispatch('selectedLevel/setMapSize', { width, height }, { root: true })
    await dispatch('selectedElement/bindScale', getters.scale, { root: true })
    dispatch('draw')
  },
  toggleLinksVisibility: ({ commit }, flag) => {
    commit('toggleLinksVisibility', flag)
  },
  toggleZonesVisibility: ({ commit }, flag) => {
    commit('toggleZonesVisibility', flag)
  },
  destroyMap({ commit }) {
    commit('destroyMap')
  },
  setTestM: ({ commit }, value) => {
    commit('setTestM', value)
  }
}

const mutations = {
  setMapD3(state, mapD3) {
    state.mapD3 = mapD3
  },
  setMapCtr(state, mapCtr) {
    state.mapCtr = mapCtr
  },
  setMap(state, map) {
    state.map = map
  },
  setCtx(state, ctx) {
    state.ctx = ctx
  },
  setZoom(state, zoom) {
    state.zoom = zoom
  },
  setTransform(state, transform) {
    state.transform = transform
  },
  toggleLinksVisibility(state, flag) {
    state.areLinksVisible = flag || !state.areLinksVisible
  },
  toggleZonesVisibility(state, flag) {
    state.areZonesVisible = flag || !state.areZonesVisible
  },
  destroyMap(state) {
    Object.assign(state, getDefaultState())
  },
  setTestM: (state, value) => {
    state.testM = value
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
  sortElementsForDraw
}
